import { useQuery } from "@apollo/client";
import { Fab, makeStyles, Tooltip, Typography } from "@material-ui/core";
import PersonAddOutlinedIcon from "@material-ui/icons/PersonAddOutlined";
import { useSnackbar } from "notistack";
import React from "react";
import ChangeRolesDialog from "../components/Users/ChangeRolesDialog";
import CreateUserDialog from "../components/Users/CreateUserDialog";
import SetUserPasswordDialog from "../components/Users/SetUserPasswordDialog";
import UserList from "../components/Users/UserList";
import {
  createUser,
  disableUser,
  enableUser,
  setUserPassword,
} from "../components/Users/utils";
import { LIST_ALL_USERS_WITH_GROUPS } from "../graphql/queries";
import { ListAllUsersWithGroups } from "../graphql/queries/listUsersWithGroups";
import useSiteTitle from "../hooks/useSiteTitle";

interface Props {}
const useStyles = makeStyles(() => ({
  root: {
    flexGrow: 1,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
}));
const Users: React.FC<Props> = () => {
  const classes = useStyles();
  const [createUserOpen, setcreateUserOpen] = React.useState<boolean>(false);
  const [passwortChangeOpen, setPasswordChangeOpen] =
    React.useState<string>("");
  const [selectedUserForRoles, setSelectedUserForRoles] =
    React.useState<string | undefined>(undefined);
  const { enqueueSnackbar } = useSnackbar();
  const {
    data: { listCognitoUsers: { items = [] } = {} } = {},
    loading,
    refetch,
  } = useQuery<ListAllUsersWithGroups>(LIST_ALL_USERS_WITH_GROUPS, {
    onError(error) {
      console.log(error);
      enqueueSnackbar("Fehler beim laden der Benutzer", { variant: "error" });
    },
    fetchPolicy: "cache-first",
  });
  useSiteTitle("Benutzerverwaltung");
  const _createUser = (username: string) => {
    return createUser(username).then(({ user }) => {
      refetch();
      return user;
    });
  };
  const _handleChangeRolesOpen = (user: string) => {
    setSelectedUserForRoles(user);
  };
  const _handleCloseChangeRoles = () => {
    setSelectedUserForRoles(undefined);
  };
  const _handleCloseCreateUser = () => {
    setcreateUserOpen(false);
  };

  const _handleChangePasswordOpen = (username: string) => {
    setPasswordChangeOpen(username);
  };
  const _handleChangePasswordClose = () => {
    setPasswordChangeOpen("");
  };

  const _setUserPassword = (username: string, password: string) => {
    return setUserPassword(username, password).then(({ password }) => {
      return password;
    });
  };

  const _changeUserState = (
    username: string,
    state: "Enabled" | "Disabled"
  ) => {
    if (state === "Enabled") {
      return disableUser(username)
        .then(() => refetch())
        .then(() => enqueueSnackbar(`Benutzer wurde deaktiviert.`));
    } else {
      return enableUser(username)
        .then(() => refetch())
        .then(() => enqueueSnackbar(`Benutzer wurde aktiviert.`));
    }
  };
  const _handleOpenCreateUser = () => {
    setcreateUserOpen(true);
  };
  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h3">Benutzer</Typography>
        <Tooltip title="Benutzer hinzufügen">
          <Fab
            aria-label="create user"
            disabled={loading}
            color={"primary"}
            onClick={_handleOpenCreateUser}
          >
            <PersonAddOutlinedIcon />
          </Fab>
        </Tooltip>
      </div>
      <UserList
        items={items || []}
        loading={loading}
        changeUserState={_changeUserState}
        changeUserRoles={_handleChangeRolesOpen}
        changeUserPassword={_handleChangePasswordOpen}
      />
      <CreateUserDialog
        open={createUserOpen}
        handleClose={_handleCloseCreateUser}
        handleSubmit={_createUser}
      />
      <SetUserPasswordDialog
        username={passwortChangeOpen}
        handleClose={_handleChangePasswordClose}
        handleSubmit={_setUserPassword}
      />
      <ChangeRolesDialog
        id={selectedUserForRoles}
        open={!!selectedUserForRoles}
        handleSubmit={_handleCloseChangeRoles}
        handleClose={_handleCloseChangeRoles}
      />
    </div>
  );
};

export default Users;
